import i18n from "@/i18n";
/**
 * ActualCellEditor — Prompt 13C §7 · §8 · §14.
 *
 * Modal editor for one employee/day Review Hours cell. Supports
 * multiple Actual worked-time blocks, overnight (work_date +
 * end_date), canonical HH:MM minute precision (NO 10-minute snap
 * because Actual is a fact, not a planning input), manager note,
 * add / edit / delete per block. Emits intents upward — the page
 * owns the API transport and the canonical Review-Hours reload.
 */
import { useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    formatDurationMinutes,
    normalizeTimeInput,
} from "@/components/workforce/workforceHelpers";
import {
    classificationRows, warningRows, statusSafeMessage,
    PROVISIONAL_CALENDAR_TEXT,
    RULE_STATUS_STANDARD_APPLIED,
} from "@/components/workforce/workforceRules";

const _HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
const _YMD_RE = /^\d{4}-\d{2}-\d{2}$/;
const _NEXT_DAY = (ymd) => {
    const [y, m, d] = ymd.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + 1);
    return dt.toISOString().slice(0, 10);
};

function _blankRow(workDate) {
    return {
        _local_key: `new-${Math.random().toString(36).slice(2)}`,
        actual_entry_id: null,
        revision: null,
        work_date: workDate,
        start_time: "",
        end_date: workDate,
        end_time: "",
        manager_note: "",
    };
}

export default function ActualCellEditor({
    open, onOpenChange,
    employeeName, workDate, existingBlocks,
    reviewed, reviewable,
    rules,
    clockEvidence,
    onCreate, onUpdate, onDelete,
    onMarkReviewed, onMarkNotReviewed,
    // Prompt 18B2-P7B §6 · §12 · §21 — manager Staff Meal
    // correction controls. `staffMeal` is the canonical current
    // fact for (employee, workDate) — `true` (YES), `false`
    // (NO), or `null` (UNANSWERED / third state). `readOnly`
    // becomes true on CLOSED periods so the toggle is disabled
    // (§10). `onStaffMealChange(nextValue)` fires the manager
    // write; the page owns the API + reload.
    staffMeal,
    onStaffMealChange,
    readOnly,
    pending,
    canCorrectReviewed,
}) {
    // Local editing rows seeded from canonical existing blocks.
    const [rows, setRows] = useState(() => {
        const seeded = (existingBlocks || []).map((b) => ({
            _local_key: b.actual_entry_id,
            actual_entry_id: b.actual_entry_id,
            revision: b.revision,
            work_date: b.work_date,
            start_time: b.start_time,
            end_date: b.end_date,
            end_time: b.end_time,
            manager_note: b.manager_note || "",
        }));
        return seeded;
    });

    const _updateRow = (key, patch) => setRows((prev) =>
        prev.map((r) => (r._local_key === key ? { ...r, ...patch } : r)));

    // Prompt 18B2-P4 §6 · §7 · §10 · §22 — friendly time input:
    // normalize Start / End on blur so managers can type "9.00",
    // "900", or "9:00" and the field snaps to canonical "HH:MM"
    // before the row's `_HHMM_RE` validation runs. Invalid input
    // (e.g. "25:00", "20.75") is left untouched so the existing
    // validation surfaces the error naturally.
    const _normalizeOnBlur = (key, field) => {
        setRows((prev) => prev.map((r) => {
            if (r._local_key !== key) return r;
            const raw = r[field];
            const normalized = normalizeTimeInput(raw);
            if (normalized === null) return r;
            if (normalized === raw) return r;
            return { ...r, [field]: normalized };
        }));
    };

    const _rowIsValid = (r) => (
        _HHMM_RE.test(r.start_time || "")
            && _HHMM_RE.test(r.end_time || "")
            && _YMD_RE.test(r.end_date || "")
    );

    // Owner rule — after approval only a manager correction with a
    // reason and explicit confirmation may change the hours.
    const [correction, setCorrection] = useState(null);
    const [correctionReason, setCorrectionReason] = useState("");
    const _hoursChanged = (r) => {
        const b = (existingBlocks || []).find(
            (x) => x.actual_entry_id === r.actual_entry_id);
        return !b || b.start_time !== r.start_time
            || b.end_date !== r.end_date || b.end_time !== r.end_time;
    };
    const _guard = (kind, payload, needed) => {
        if (reviewed && needed && !canCorrectReviewed) {
            setCorrection({ kind: "denied" });
            return;
        }
        if (reviewed && needed) {
            setCorrectionReason("");
            setCorrection({ kind, payload });
            return;
        }
        ({ create: onCreate, update: onUpdate, delete: onDelete })[kind](payload);
    };
    const _confirmCorrection = () => {
        const reason = correctionReason.trim();
        if (!correction || !reason) return;
        const extra = { correction_reason: reason, confirm_reviewed_correction: true };
        ({ create: onCreate, update: onUpdate, delete: onDelete })[correction.kind](
            { ...correction.payload, ...extra });
        setCorrection(null);
    };

    const _save = (r) => {
        if (!_rowIsValid(r)) return;
        if (r.actual_entry_id) {
            _guard("update", {
                actual_entry_id: r.actual_entry_id,
                expected_revision: r.revision,
                start_time: r.start_time,
                end_date: r.end_date,
                end_time: r.end_time,
                manager_note: r.manager_note || "",
            }, _hoursChanged(r));
        } else {
            _guard("create", {
                work_date: workDate,
                start_time: r.start_time,
                end_date: r.end_date,
                end_time: r.end_time,
                manager_note: r.manager_note || "",
            }, true);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="workforce-actual-editor"
                className="max-w-2xl"
            >
                <DialogHeader>
                    <DialogTitle
                        data-testid="workforce-actual-editor-title"
                    >
                        {i18n.t("wf.actualCellEditor.reviewActualHours", "Review actual hours")} · {employeeName} · {workDate}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    {rows.map((r) => (
                        <div
                            key={r._local_key}
                            data-testid={`workforce-actual-row-${r._local_key}`}
                            className="rounded-md border border-neutral-200
                                        bg-white p-3 space-y-2"
                        >
                            <div className="flex flex-wrap items-end gap-2">
                                <div className="flex flex-col">
                                    <Label className="text-xs text-neutral-500">
                                        {i18n.t("wf.actualCellEditor.start", "Start")}
                                    </Label>
                                    <Input
                                        data-testid={`workforce-actual-start-${r._local_key}`}
                                        value={r.start_time}
                                        onChange={(e) => _updateRow(
                                            r._local_key,
                                            { start_time: e.target.value })}
                                        onBlur={() => _normalizeOnBlur(
                                            r._local_key, "start_time")}
                                        placeholder={i18n.t("wf.actualCellEditor.hhMm", "HH:MM")}
                                        className="w-24"
                                    />
                                </div>
                                <div className="flex flex-col">
                                    <Label className="text-xs text-neutral-500">
                                        {i18n.t("wf.actualCellEditor.endDate", "End date")}
                                    </Label>
                                    <Input
                                        data-testid={`workforce-actual-enddate-${r._local_key}`}
                                        value={r.end_date}
                                        onChange={(e) => _updateRow(
                                            r._local_key,
                                            { end_date: e.target.value })}
                                        placeholder={i18n.t("wf.actualCellEditor.yyyyMmDd", "YYYY-MM-DD")}
                                        className="w-36"
                                    />
                                </div>
                                <div className="flex flex-col">
                                    <Label className="text-xs text-neutral-500">
                                        {i18n.t("wf.actualCellEditor.end", "End")}
                                    </Label>
                                    <Input
                                        data-testid={`workforce-actual-end-${r._local_key}`}
                                        value={r.end_time}
                                        onChange={(e) => _updateRow(
                                            r._local_key,
                                            { end_time: e.target.value })}
                                        onBlur={() => _normalizeOnBlur(
                                            r._local_key, "end_time")}
                                        placeholder={i18n.t("wf.actualCellEditor.hhMm", "HH:MM")}
                                        className="w-24"
                                    />
                                </div>
                                <button
                                    type="button"
                                    data-testid={`workforce-actual-overnight-${r._local_key}`}
                                    onClick={() => _updateRow(r._local_key, {
                                        end_date: _NEXT_DAY(r.work_date),
                                    })}
                                    className="text-xs text-neutral-500
                                                underline"
                                >
                                    +1 day
                                </button>
                            </div>
                            <div>
                                <Label className="text-xs text-neutral-500">
                                    {i18n.t("wf.actualCellEditor.managerNote", "Manager note")}
                                </Label>
                                <Input
                                    data-testid={`workforce-actual-note-${r._local_key}`}
                                    value={r.manager_note || ""}
                                    onChange={(e) => _updateRow(
                                        r._local_key,
                                        { manager_note: e.target.value })}
                                    placeholder={i18n.t("wf.actualCellEditor.optional", "Optional")}
                                />
                            </div>
                            <div className="flex justify-between">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={pending || !_rowIsValid(r)}
                                    onClick={() => _save(r)}
                                    data-testid={`workforce-actual-save-${r._local_key}`}
                                >
                                    {i18n.t("common.save", "Save")}
                                </Button>
                                {r.actual_entry_id && (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={pending}
                                        onClick={() => _guard("delete", {
                                            actual_entry_id: r.actual_entry_id,
                                            expected_revision: r.revision,
                                        }, true)}
                                        data-testid={`workforce-actual-delete-${r._local_key}`}
                                    >
                                        {i18n.t("common.delete", "Delete")}
                                    </Button>
                                )}
                            </div>
                        </div>
                    ))}
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={pending}
                        onClick={() => setRows(
                            (prev) => [...prev, _blankRow(workDate)])}
                        data-testid="workforce-actual-add-block"
                    >
                        {i18n.t("wf.actualCellEditor.addActualBlock", "Add actual block")}
                    </Button>
                    {correction?.kind === "denied" && (
                        <div data-testid="workforce-actual-correction-denied"
                             className="rounded-md border border-red-300 bg-red-50 p-3 text-xs text-red-900">
                            {i18n.t("wf.actualCellEditor.theseHoursAreAlreadyApprovedOnly", "These hours are already approved. Only a manager or corporate account can correct them.")}
                        </div>
                    )}
                    {correction && correction.kind !== "denied" && (
                        <div data-testid="workforce-actual-correction"
                             className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2 text-xs text-amber-900">
                            <div className="font-medium">
                                {i18n.t("wf.actualCellEditor.theseHoursAreAlreadyApprovedCorrecting", "These hours are already approved. Correcting them keeps the day confirmed and is recorded with your name and reason.")}
                            </div>
                            <textarea
                                data-testid="workforce-actual-correction-reason"
                                className="w-full rounded border border-amber-300 bg-white p-2 text-sm text-neutral-900"
                                rows={2} maxLength={500} placeholder={i18n.t("wf.actualCellEditor.reasonForTheCorrection", "Reason for the correction")}
                                value={correctionReason}
                                onChange={(e) => setCorrectionReason(e.target.value)} />
                            <div className="flex gap-2">
                                <Button size="sm" data-testid="workforce-actual-correction-confirm"
                                        disabled={pending || !correctionReason.trim()}
                                        onClick={_confirmCorrection}>
                                    {i18n.t("wf.actualCellEditor.confirmCorrection", "Confirm correction")}
                                </Button>
                                <Button size="sm" variant="outline"
                                        data-testid="workforce-actual-correction-cancel"
                                        onClick={() => setCorrection(null)}>
                                    {i18n.t("common.cancel", "Cancel")}
                                </Button>
                            </div>
                        </div>
                    )}
                    <ClockEvidenceSection
                        sessions={clockEvidence}
                        onUseClockTimes={(session) => setRows((prev) => [
                            ...prev,
                            {
                                ..._blankRow(workDate),
                                start_time: session.start_time,
                                end_date: session.end_date || workDate,
                                end_time: session.end_time || "",
                            },
                        ])}
                        pending={pending}
                    />
                    <StaffMealSection
                        staffMeal={staffMeal}
                        onStaffMealChange={onStaffMealChange}
                        readOnly={readOnly}
                        pending={pending}
                    />
                    <RulesSection rules={rules} />
                </div>
                <DialogFooter className="mt-4 flex items-center justify-between">
                    {reviewable && !reviewed && (
                        <Button
                            data-testid="workforce-actual-mark-reviewed"
                            disabled={pending}
                            onClick={onMarkReviewed}
                        >
                            {i18n.t("wf.actualCellEditor.markReviewed", "Mark reviewed")}
                        </Button>
                    )}
                    {reviewable && reviewed && (
                        <Button
                            variant="outline"
                            data-testid="workforce-actual-mark-not-reviewed"
                            disabled={pending}
                            onClick={onMarkNotReviewed}
                        >
                            {i18n.t("wf.actualCellEditor.markNotReviewed", "Mark not reviewed")}
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        data-testid="workforce-actual-editor-close"
                    >
                        {i18n.t("common.close", "Close")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// Prompt 18B2-P7B §6 · §12 · §21 — manager Staff Meal correction
// controls. Renders three explicit choices (Yes / No / Not
// answered) mapping to the canonical three-way semantics
// (true / false / null · §1 · §6). Disabled on CLOSED periods
// (§10) — the manager sees the frozen answer but cannot mutate.
// No hours math here (§11); the manager write persists the fact
// only, and the page reloads Review Hours so time-derived status
// recomputes automatically (§12).
function StaffMealSection({
    staffMeal, onStaffMealChange, readOnly, pending,
}) {
    if (!onStaffMealChange) return null;
    const _cur = (staffMeal === true) ? "yes"
        : (staffMeal === false) ? "no"
        : "unanswered";
    const _btn = (key, label, next) => (
        <button
            type="button"
            data-testid={`workforce-actual-staff-meal-${key}`}
            aria-pressed={_cur === key}
            disabled={readOnly || pending}
            onClick={() => onStaffMealChange(next)}
            className={`text-xs rounded-md border px-3 py-1
                        transition-colors disabled:opacity-60
                        disabled:cursor-not-allowed ${
                _cur === key
                    ? "border-neutral-900 bg-neutral-900 text-white"
                    : "border-neutral-300 text-neutral-700"}`}
        >
            {label}
        </button>
    );
    return (
        <div
            data-testid="workforce-actual-staff-meal"
            data-meal-state={_cur}
            data-readonly={readOnly ? "true" : undefined}
            className="mt-2 rounded-md border border-neutral-200
                        bg-white p-3 text-xs text-neutral-700
                        space-y-2"
        >
            <div className="font-medium text-neutral-700">
                {i18n.t("wf.actualCellEditor.staffMeal", "Staff meal")}
            </div>
            <div className="flex gap-2">
                {_btn("yes", i18n.t("common.yes", "Yes"), true)}
                {_btn("no", i18n.t("common.no", "No"), false)}
                {_btn("unanswered", i18n.t("wf.actualCellEditor.notAnswered", "Not answered"), null)}
            </div>
        </div>
    );
}


// Prompt 15B §17 · §18 — read-only Clock Evidence section. Shows
// paired sessions with human-friendly time / duration text and an
// explicit "Use clock times" button per COMPLETE session. The
// button ONLY prefills a new actual block; it never writes to the
// server. Manager must still click the existing Save (§18).
function ClockEvidenceSection({ sessions, onUseClockTimes, pending }) {
    if (!Array.isArray(sessions) || sessions.length === 0) return null;
    return (
        <div
            data-testid="workforce-actual-clock-evidence"
            className="mt-2 rounded-md border border-neutral-200
                        bg-neutral-50 p-3 text-xs text-neutral-700
                        space-y-2"
        >
            <div className="font-medium text-neutral-700">
                {i18n.t("wf.actualCellEditor.clockEvidence", "Clock evidence")}
            </div>
            <ul className="space-y-1">
                {sessions.map((s, idx) => {
                    const testKey = s.clock_in_event_id || `open-${idx}`;
                    if (s.status !== "COMPLETE") {
                        return (
                            <li
                                key={testKey}
                                data-testid={`workforce-actual-clock-evidence-open-${testKey}`}
                                className="text-neutral-700"
                            >
                                {i18n.t("wf.actualCellEditor.clockedInNoOut", "Clocked in at {{time}} — no clock out yet", { time: s.start_time })}
                            </li>
                        );
                    }
                    const overnight = s.end_date && s.end_date !== s.work_date;
                    return (
                        <li
                            key={testKey}
                            data-testid={`workforce-actual-clock-evidence-session-${testKey}`}
                            className="flex justify-between items-baseline
                                        gap-3"
                        >
                            <span>
                                {s.start_time}–{s.end_time}
                                {overnight ? ` ${i18n.t("wf.actualCellEditor.plusOneDay", "(+1 day)")}` : ""}
                                <span className="text-neutral-500 ml-2">
                                    {formatDurationMinutes(
                                        s.duration_minutes)}
                                </span>
                            </span>
                            <button
                                type="button"
                                data-testid={`workforce-actual-use-clock-${testKey}`}
                                onClick={() => onUseClockTimes(s)}
                                disabled={pending}
                                className="text-neutral-700 underline
                                            disabled:opacity-50"
                            >
                                {i18n.t("wf.actualCellEditor.useClockTimes", "Use clock times")}
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}


// Prompt 14D §14 · §15 · §16 — read-only Rules section. Renders
// classifications with human-friendly labels and duration in
// `H h M min` format only, plus friendly compliance-warning
// sentences. Never displays raw warning codes, raw minutes,
// money, or salary. Safe rule-status messages take precedence.
function RulesSection({ rules }) {
    if (!rules) return null;
    const status = rules.rules_status;
    const safeMsg = statusSafeMessage(status);
    if (safeMsg) {
        return (
            <div
                data-testid="workforce-actual-rules"
                className="mt-2 rounded-md border border-neutral-200
                            bg-neutral-50 p-3 text-xs text-neutral-600"
            >
                <div
                    data-testid="workforce-actual-rules-status"
                    className="font-medium text-neutral-700"
                >
                    {i18n.t("wf.actualCellEditor.rules", "Rules")}
                </div>
                <div
                    data-testid={`workforce-actual-rules-status-${status}`}
                    className="mt-0.5"
                >
                    {safeMsg}
                </div>
            </div>
        );
    }
    if (status && status !== RULE_STATUS_STANDARD_APPLIED) return null;
    const cRows = classificationRows(rules.classifications);
    const wRows = warningRows(rules.compliance_warnings);
    if (cRows.length === 0 && wRows.length === 0) return null;
    return (
        <div
            data-testid="workforce-actual-rules"
            className="mt-2 rounded-md border border-neutral-200
                        bg-neutral-50 p-3 text-xs text-neutral-700
                        space-y-2"
        >
            <div className="font-medium text-neutral-700">{i18n.t("wf.actualCellEditor.rules", "Rules")}</div>
            {rules.provisional && cRows.length > 0 && (
                <div
                    data-testid="workforce-actual-rules-provisional"
                    className="text-neutral-500"
                >
                    {i18n.t("wf.workforceRules.PROVISIONAL_CALENDAR_TEXT", PROVISIONAL_CALENDAR_TEXT)}
                </div>
            )}
            {cRows.length > 0 && (
                <ul
                    data-testid="workforce-actual-rules-classifications"
                    className="space-y-0.5"
                >
                    {cRows.map((r) => (
                        <li
                            key={r.category}
                            data-testid={`workforce-actual-rules-class-${r.category}`}
                            className="flex justify-between gap-4"
                        >
                            <span>{r.label}</span>
                            <span
                                data-testid={`workforce-actual-rules-class-duration-${r.category}`}
                                className="text-neutral-500"
                            >
                                {r.duration_text}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
            {wRows.length > 0 && (
                <ul
                    data-testid="workforce-actual-rules-warnings"
                    className="space-y-0.5"
                >
                    {wRows.map((w) => (
                        <li
                            key={w.code}
                            data-testid={`workforce-actual-rules-warning-${w.code}`}
                            className="text-neutral-700"
                        >
                            {w.label}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
